import { XMarkIcon } from '@heroicons/react/16/solid';
import { useEffect, useState } from 'react';
import { Button, Input } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { ChatEnum, EventType } from '../../../../Chat.enum.ts';
import { updateEventAttachmentParser } from '../../../../Chat.functions.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { tAttachment } from '../../../../Chat.types.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import LegacyInput from '../LegacyInput/LegacyInput.component.tsx';
import useNotesAction from './NotesAction.hook.ts';
import './NotesAction.styles.scss';
import { getModuleName } from '../../../../Chat.utils.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';

export default function NotesAction() {
  const { trackEvent } = useAnalytics();
  const { selectedNote, setSelectedNote, setMessages, selectedChat } =
    useChatContext();
  const { loading, createNewNote, updateEvent } = useNotesAction();
  const {
    messageBarValue,
    setMessageBarValue,
    setSelectedAction,
    draftMessages,
    setDraftMessages,
  } = useMessageBarContext();
  const { t } = useTranslation('chat/chat');

  const [title, setTitle] = useState<string>('');
  const [noteAttachments, setNoteAttachments] = useState<tAttachment[]>();

  useEffect(() => {
    if (selectedNote) {
      setTitle(selectedNote.title);
      setNoteAttachments(selectedNote.attachments);
      setMessageBarValue(selectedNote.content);
    }
  }, [selectedNote?.id]);

  function handleButton() {
    const nextDraftMessages = draftMessages ? [...draftMessages] : [];
    let currentDraft = -1;

    if (selectedChat?.leadId && Array.isArray(draftMessages)) {
      currentDraft = draftMessages.findIndex(
        (draft) => draft.leadId === selectedChat.leadId,
      );
    }

    if (selectedNote?.id) {
      updateEvent(selectedNote.id, {
        type: EventType.NOTE,
        note: {
          title: title,
        },
        content: {
          text: messageBarValue,
          attachments: noteAttachments?.map((attachment) =>
            updateEventAttachmentParser(attachment),
          ),
        },
      });
      setMessages((prevMessages) => {
        if (!prevMessages) return prevMessages;

        const newMessages = {
          ...prevMessages,
          events: prevMessages.events.map((event) => {
            if (event.id === selectedNote.id) {
              return {
                ...event,
                content: {
                  ...event.content,
                  text: messageBarValue,
                  attachments: noteAttachments,
                },
                note: {
                  title: title,
                },
              };
            }

            return event;
          }),
        };

        trackEvent('front.chat.actions.notes.create.save_btn.click', {
          displayName: 'Crear y guardar Notaa',
          description: 'Cuántas notas nuevas se crearon',
          ctaType: getModuleName(),
        });

        return newMessages;
      });
    } else {
      createNewNote(title, noteAttachments);
    }
    setTitle('');
    setSelectedNote(null);
    if (currentDraft > -1) {
      nextDraftMessages.splice(currentDraft, 1);
      setDraftMessages(nextDraftMessages);
    }
  }

  return (
    <div className="form-control absolute bottom-0 w-full">
      <div className="NotesAction">
        <div className="NotesAction__Left">
          <p className="font-medium font-sans">{t('notesAction.createNote')}</p>
          <Input
            value={title}
            size="sm"
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('notesAction.title')}
            className="NotesAction__Left__Input"
            type="text"
          />
        </div>

        <Button shape="circle" size="sm" color="ghost">
          <XMarkIcon
            onClick={() => {
              setSelectedAction(ChatEnum.CHAT);
              setSelectedNote(null);
            }}
            className="NotesAction__XIcon"
          />
        </Button>
      </div>
      <LegacyInput
        attachments={noteAttachments}
        setNewAttachments={setNoteAttachments}
        actionButtonText={
          selectedNote?.id ? t('notesAction.update') : t('notesAction.save')
        }
        actionButtonLoading={loading}
        actionButtonDisabled={!title.length}
        handleActionButton={handleButton}
      />
    </div>
  );
}
